import { motion } from 'framer-motion';
import MarketingLayout from '../components/MarketingLayout';
import SEO from '../components/SEO';

const fadeUp = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true } };

export default function TermsOfServicePage() {
  return (
    <MarketingLayout>
      <SEO
        title="Terms of Service"
        description="PAFHub Terms of Service — the agreement governing your use of our H-1B PAF compliance management platform."
        path="/terms"
      />
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 bg-gradient-to-br from-indigo-50/80 via-white to-sky-50/50">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <motion.div {...fadeUp} transition={{ duration: 0.5 }}>
            <p className="text-sm font-semibold text-indigo-600 uppercase tracking-wider mb-3">Legal</p>
            <h1 className="text-4xl lg:text-5xl font-bold text-[#0F172A] tracking-tight mb-4">Terms of Service</h1>
            <p className="text-[#64748B]">Last updated: April 1, 2026</p>
          </motion.div>
        </div>
      </section>

      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 prose prose-slate prose-headings:text-[#0F172A] prose-headings:font-bold prose-p:text-[#475569] prose-p:leading-[1.8] prose-li:text-[#475569] prose-strong:text-[#0F172A] max-w-none">

          <h2>1. Acceptance of Terms</h2>
          <p>By accessing or using PAFHub (the "Service"), operated by PAFHub ("we," "our," or "us"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, you may not use the Service. These Terms apply to all users of the platform, including account holders, team members, and visitors accessing public PAF links.</p>

          <h2>2. Description of Service</h2>
          <p>PAFHub is a cloud-based platform designed to help employers manage Public Access Files (PAFs) for H-1B, H-1B1, and E-3 visa workers as required by the U.S. Department of Labor. The Service includes:</p>
          <ul>
            <li>Employee and visa information management</li>
            <li>Document upload, storage, and organization</li>
            <li>PAF compliance tracking and status monitoring</li>
            <li>Automated PAF packet generation (PDF)</li>
            <li>Public access file sharing via secure links</li>
            <li>Compliance reminder notifications</li>
            <li>Audit trail and activity logging</li>
          </ul>

          <h2>3. Account Registration</h2>
          <p>To use PAFHub, you must create an account by providing accurate and complete information. You are responsible for:</p>
          <ul>
            <li>Maintaining the confidentiality of your login credentials</li>
            <li>All activity that occurs under your account</li>
            <li>Notifying us immediately of any unauthorized use of your account</li>
          </ul>
          <p>We reserve the right to suspend or terminate accounts that violate these Terms or engage in fraudulent activity.</p>

          <h2>4. Subscription Plans & Payments</h2>
          <p>PAFHub offers multiple subscription tiers (Free, Starter, Professional, Enterprise). By subscribing to a paid plan:</p>
          <ul>
            <li>You authorize us to charge the payment method on file through our payment processor, Stripe</li>
            <li>Subscription fees are billed according to your selected plan</li>
            <li>You may upgrade or change your plan at any time through the Settings page</li>
            <li>Refunds are handled on a case-by-case basis; contact us at compliance@pafhub.com for refund requests</li>
          </ul>

          <h2>5. Your Data & Content</h2>
          <p>You retain ownership of all data, documents, and content you upload to PAFHub ("Your Content"). By using the Service, you grant us a limited license to store, process, and display Your Content solely for the purpose of providing the Service to you.</p>
          <p>You are solely responsible for:</p>
          <ul>
            <li>The accuracy and legality of the data you upload</li>
            <li>Ensuring you have the right to upload employee information and documents</li>
            <li>Maintaining your own backup copies of critical documents</li>
          </ul>

          <h2>6. Compliance Disclaimer</h2>
          <p><strong>PAFHub is a document management and organizational tool. It is not a law firm and does not provide legal advice.</strong></p>
          <p>While PAFHub helps you organize and manage Public Access Files, it is your responsibility to:</p>
          <ul>
            <li>Ensure compliance with all applicable Department of Labor regulations</li>
            <li>Verify that your PAFs contain all required documents</li>
            <li>Consult with qualified immigration counsel for legal guidance</li>
            <li>Meet all filing deadlines and regulatory requirements</li>
          </ul>
          <p>PAFHub's compliance tracking, alerts, and checklists are provided as organizational aids and do not constitute legal compliance certification.</p>

          <h2>7. Public Access File Sharing</h2>
          <p>When you generate a public access link for a PAF, the contents of that PAF become accessible to anyone with the link. You are responsible for:</p>
          <ul>
            <li>Deciding which PAFs to make publicly accessible</li>
            <li>Revoking public access when it is no longer needed</li>
            <li>Ensuring that shared PAFs do not contain information beyond what is required for public access</li>
          </ul>

          <h2>8. Prohibited Uses</h2>
          <p>You agree not to:</p>
          <ul>
            <li>Use the Service for any unlawful purpose</li>
            <li>Upload malicious files, viruses, or harmful code</li>
            <li>Attempt to gain unauthorized access to other users' accounts or data</li>
            <li>Reverse engineer, decompile, or disassemble any part of the Service</li>
            <li>Use the Service to store data unrelated to PAF compliance management</li>
            <li>Resell or redistribute access to the Service without our written consent</li>
          </ul>

          <h2>9. Service Availability</h2>
          <p>We strive to maintain high availability of the PAFHub platform. However, we do not guarantee uninterrupted access. The Service may be temporarily unavailable due to maintenance, updates, or circumstances beyond our control. We will make reasonable efforts to provide advance notice of scheduled maintenance.</p>

          <h2>10. Account Deletion</h2>
          <p>You may delete your account at any time through the Settings page. Upon deletion:</p>
          <ul>
            <li>All your data, including employee records, PAF files, documents, and audit logs, will be permanently and irreversibly deleted</li>
            <li>Any active subscription will be canceled</li>
            <li>Public access links will stop working immediately</li>
          </ul>

          <h2>11. Limitation of Liability</h2>
          <p>To the maximum extent permitted by law, PAFHub and its officers, directors, employees, and agents shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the Service, including but not limited to:</p>
          <ul>
            <li>Penalties or fines resulting from DOL audits or compliance failures</li>
            <li>Loss of data due to account deletion or system failures</li>
            <li>Business interruption or loss of revenue</li>
          </ul>
          <p>Our total liability shall not exceed the amount you have paid to PAFHub in the twelve (12) months preceding the claim.</p>

          <h2>12. Indemnification</h2>
          <p>You agree to indemnify and hold PAFHub harmless from any claims, damages, or expenses arising from your use of the Service, your violation of these Terms, or your violation of any third party's rights.</p>

          <h2>13. Changes to Terms</h2>
          <p>We may update these Terms from time to time. We will notify users of material changes by email or through a notice on the platform. Continued use of the Service after changes become effective constitutes acceptance of the updated Terms.</p>

          <h2>14. Governing Law</h2>
          <p>These Terms shall be governed by and construed in accordance with the laws of the State of Texas, without regard to its conflict of law provisions.</p>

          <h2>15. Contact Us</h2>
          <p>If you have questions about these Terms of Service, please contact us at:</p>
          <ul>
            <li>Email: compliance@pafhub.com</li>
            <li>Website: pafhub.com/contact</li>
          </ul>
        </div>
      </section>
    </MarketingLayout>
  );
}
